'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { finishNavigation, startNavigation, useIsNavigating } from './routerProgress';

/**
 * While a new page loads: a thin progress bar at the top of the screen, and a transparent layer that
 * holds back clicks (with a "working" cursor) so the same button cannot be pressed twice.
 */
export default function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isNavigating = useIsNavigating();

  // The new page has arrived
  useEffect(() => {
    finishNavigation();
  }, [pathname, searchParams]);

  // Plain links (<a> / <Link>) start the same indicator
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      startNavigation(anchor.href);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  if (!isNavigating) return null;

  return (
    <>
      <div role="progressbar" aria-label="Loading page" className="fixed inset-x-0 top-0 z-[10000] h-1 overflow-hidden bg-sky-100">
        <div className="h-full w-1/3 animate-navigation-progress rounded-full bg-sky-500" />
      </div>
      <div aria-hidden className="fixed inset-0 z-[9999] cursor-progress" />
    </>
  );
}
